import React, { useState, useRef } from 'react';
import { csvProcessingService } from '../services/CSVProcessingService';
import { reportStorageService, StoredReport } from '../services/ReportStorageService';
import MarketingReport from '../components/MarketingReport';
import CCMarketingReport from '../components/reports/CCMarketingReport';
import { ReportData } from '../types/reportTypes';
import { combineReports } from '../utils/reportAggregationUtils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * GenerateReportPage - 4-step wizard for CSV upload and report generation
 *
 * Step 1: Configure report type and period
 * Step 2: Upload CSV files
 * Step 3: Preview and verify data
 * Step 4: View and export final report
 */
const GenerateReportPage: React.FC = () => {
  // ============================================================================
  // State Management
  // ============================================================================
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [reportType, setReportType] = useState<'standard' | 'cody' | null>(null);
  const [period, setPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [salesFile, setSalesFile] = useState<File | null>(null);
  const [adSpendFile, setAdSpendFile] = useState<File | null>(null);
  const [salesFileStatus, setSalesFileStatus] = useState<{valid: boolean; message: string} | null>(null);
  const [adSpendFileStatus, setAdSpendFileStatus] = useState<{valid: boolean; message: string} | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [generationMode, setGenerationMode] = useState<'monthly' | 'quarterly' | 'ytd'>('monthly');
  const [selectedQuarter, setSelectedQuarter] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [availableReports, setAvailableReports] = useState<StoredReport[]>([]);
  const reportRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // Constants
  // ============================================================================
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // ============================================================================
  // Utility Functions
  // ============================================================================
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);

  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;

  const quarterNames = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];

  const getExpectedMonths = (): number[] => {
    if (generationMode === 'quarterly') {
      const start = (selectedQuarter - 1) * 3 + 1;
      return [start, start + 1, start + 2];
    } else if (generationMode === 'ytd') {
      return Array.from({ length: period.month }, (_, i) => i + 1);
    }
    return [period.month];
  };

  const loadAvailableReports = () => {
    const allReports = reportStorageService.getReports(); // sorted newest first
    const expectedMonths = getExpectedMonths();

    // Filter by type, year, and monthly-only (exclude quarterly/YTD), deduplicate by month
    const monthMap = new Map<number, StoredReport>();
    allReports
      .filter(r =>
        r.reportType === reportType &&
        r.period.year === period.year &&
        (!r.generationMode || r.generationMode === 'monthly') &&
        expectedMonths.includes(r.period.month)
      )
      .forEach(r => {
        if (!monthMap.has(r.period.month)) {
          monthMap.set(r.period.month, r); // First match (newest) wins
        }
      });

    const filtered = Array.from(monthMap.values()).sort((a, b) => a.period.month - b.period.month);
    setAvailableReports(filtered);
  };

  const processAggregation = () => {
    if (availableReports.length === 0) {
      setError('No reports available to combine');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const reportsData = availableReports.map(r => r.data);
      const combined = combineReports(reportsData);

      if (generationMode === 'quarterly') {
        const firstMonth = (selectedQuarter - 1) * 3 + 1;
        combined.period = {
          month: firstMonth,
          year: period.year,
          quarter: selectedQuarter,
          startMonth: firstMonth,
          endMonth: firstMonth + 2
        };
      } else {
        combined.period = {
          month: period.month,
          year: period.year,
          ytd: true,
          startMonth: 1,
          endMonth: period.month,
          throughMonth: period.month
        };
      }

      setReportData(combined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to combine reports');
    } finally {
      setIsProcessing(false);
    }
  };

  const getReportTitle = (): string => {
    const typePrefix = reportType === 'cody' ? 'Cody Direct Sales' : 'ROI Exteriors Marketing';

    if (generationMode === 'quarterly') {
      return `${typePrefix} Report - Q${selectedQuarter} ${period.year}`;
    } else if (generationMode === 'ytd') {
      return `${typePrefix} Report - YTD January-${monthNames[period.month - 1]} ${period.year}`;
    }

    return reportType === 'cody'
      ? `Cody Direct Sales Report - ${monthNames[period.month - 1]} ${period.year}`
      : `ROI Exteriors Marketing Report - ${monthNames[period.month - 1]} ${period.year}`;
  };

  // ============================================================================
  // File Handlers
  // ============================================================================
  const handleSalesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSalesFile(file);

    // Basic validation - check file type and size
    if (!file.name.endsWith('.csv')) {
      setSalesFileStatus({ valid: false, message: 'Please select a CSV file' });
      return;
    }

    if (file.size === 0) {
      setSalesFileStatus({ valid: false, message: 'File is empty' });
      return;
    }

    setSalesFileStatus({ valid: true, message: `File loaded: ${file.name}` });
  };

  const handleAdSpendFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAdSpendFile(file);

    if (!file.name.endsWith('.csv')) {
      setAdSpendFileStatus({ valid: false, message: 'Please select a CSV file' });
      return;
    }

    if (file.size === 0) {
      setAdSpendFileStatus({ valid: false, message: 'File is empty' });
      return;
    }

    setAdSpendFileStatus({ valid: true, message: `File loaded: ${file.name}` });
  };

  // ============================================================================
  // CSV Processing
  // ============================================================================
  const processCSVs = async () => {
    if (!salesFile || !reportType) return;

    setIsProcessing(true);
    setError(null);

    try {
      const data = await csvProcessingService.generateReportFromFiles(
        salesFile,
        adSpendFile,
        reportType,
        period
      );

      if (data.jobData.length === 0) {
        setError('No valid jobs found in the CSV file. Check the format and try again.');
      } else {
        setReportData(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process CSV files');
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // Report Actions
  // ============================================================================
  const handlePrint = () => {
    window.print();
  };

  const generatePDF = async () => {
    if (!reportRef.current) return;

    try {
      const prefix = reportType === 'cody' ? 'ROI_Cody_Direct_Sales' : 'ROI_Marketing_Report';
      let periodSuffix: string;
      if (generationMode === 'quarterly') {
        periodSuffix = `Q${selectedQuarter}_${period.year}`;
      } else if (generationMode === 'ytd') {
        periodSuffix = `YTD_Jan-${monthNames[period.month - 1]}_${period.year}`;
      } else {
        periodSuffix = `${monthNames[period.month - 1]}_${period.year}`;
      }
      const fileName = `${prefix}_${periodSuffix}.pdf`;

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;

      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;

      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }

      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }

      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleSave = () => {
    if (!reportData || !reportType) return;

    const title = getReportTitle();
    const savePeriod = generationMode === 'quarterly'
      ? { month: (selectedQuarter - 1) * 3 + 1, year: period.year }
      : period;

    reportStorageService.saveReport(title, reportType, savePeriod, reportData, generationMode);
    setSaved(true);
  };

  const handleGenerateAnother = () => {
    // Reset all state
    setStep(1);
    setReportType(null);
    setSalesFile(null);
    setAdSpendFile(null);
    setSalesFileStatus(null);
    setAdSpendFileStatus(null);
    setReportData(null);
    setError(null);
    setSaved(false);
    setGenerationMode('monthly');
    setSelectedQuarter(Math.ceil((new Date().getMonth() + 1) / 3));
    setAvailableReports([]);
  };

  // ============================================================================
  // Step Navigation
  // ============================================================================
  const handleNextStep = () => {
    if (step === 1) {
      if (generationMode !== 'monthly') {
        loadAvailableReports();
      }
      setStep(2);
    } else if (step === 2) {
      setStep(3);
      if (generationMode === 'monthly') {
        processCSVs();
      } else {
        processAggregation();
      }
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleBackStep = () => {
    if (step > 1) {
      setStep((step - 1) as 1 | 2 | 3 | 4);
      // Clear error when going back
      setError(null);
    }
  };

  // ============================================================================
  // Step Indicator Component
  // ============================================================================
  const StepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {[1, 2, 3, 4].map((stepNum, index) => (
          <React.Fragment key={stepNum}>
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-semibold
                  ${stepNum < step ? 'bg-green-500 text-white' : ''}
                  ${stepNum === step ? 'bg-blue-600 text-white' : ''}
                  ${stepNum > step ? 'bg-gray-200 text-gray-500' : ''}
                `}
              >
                {stepNum}
              </div>
              <span className="text-xs mt-2 text-gray-600">
                {stepNum === 1 && 'Configure'}
                {stepNum === 2 && (generationMode === 'monthly' ? 'Upload' : 'Select')}
                {stepNum === 3 && 'Preview'}
                {stepNum === 4 && 'Report'}
              </span>
            </div>
            {index < 3 && (
              <div
                className={`
                  flex-1 h-1 mx-2 mb-6
                  ${stepNum < step ? 'bg-green-500' : 'bg-gray-200'}
                `}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // ============================================================================
  // Step 1: Configure Report
  // ============================================================================
  const renderStep1 = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Generate Marketing Report
        </h2>
        <p className="text-gray-600 mb-6">
          Upload CSV files to generate a marketing performance report
        </p>

        {/* Report Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Report Type
          </label>
          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="reportType"
                value="standard"
                checked={reportType === 'standard'}
                onChange={(e) => setReportType(e.target.value as 'standard')}
                className="mt-1 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">Standard Monthly Report</div>
                <div className="text-sm text-gray-600">
                  Uses tiered commission (0/4/7/10%), 1% manager fee, 15.5% company OH
                </div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="reportType"
                value="cody"
                checked={reportType === 'cody'}
                onChange={(e) => setReportType(e.target.value as 'cody')}
                className="mt-1 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">Cody Direct Sales Report</div>
                <div className="text-sm text-gray-600">
                  Uses flat 13% company OH, 50% net profit commission split
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Generation Mode */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Generation Mode
          </label>
          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="generationMode"
                value="monthly"
                checked={generationMode === 'monthly'}
                onChange={() => setGenerationMode('monthly')}
                className="mt-1 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">Monthly Report (Upload CSV)</div>
                <div className="text-sm text-gray-600">
                  Upload a sales CSV to generate a single monthly report
                </div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="generationMode"
                value="quarterly"
                checked={generationMode === 'quarterly'}
                onChange={() => setGenerationMode('quarterly')}
                className="mt-1 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">Quarterly Report (Combine Saved)</div>
                <div className="text-sm text-gray-600">
                  Combine 3 saved monthly reports into a quarterly report
                </div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="generationMode"
                value="ytd"
                checked={generationMode === 'ytd'}
                onChange={() => setGenerationMode('ytd')}
                className="mt-1 mr-3"
              />
              <div>
                <div className="font-medium text-gray-900">Year-to-Date Report (Combine Saved)</div>
                <div className="text-sm text-gray-600">
                  Combine saved monthly reports from January through a selected month
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Period Selection */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {generationMode === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <select
                value={period.month}
                onChange={(e) => setPeriod({ ...period, month: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {monthNames.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {generationMode === 'quarterly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quarter
              </label>
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {quarterNames.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {generationMode === 'ytd' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Through Month
              </label>
              <select
                value={period.month}
                onChange={(e) => setPeriod({ ...period, month: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {monthNames.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <input
              type="number"
              value={period.year}
              onChange={(e) => setPeriod({ ...period, year: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="2020"
              max="2030"
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-end">
          <button
            onClick={handleNextStep}
            disabled={!reportType}
            className={`
              px-6 py-2 rounded-md font-medium transition-colors
              ${reportType
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Step 2: Upload CSVs (Monthly) or Select Reports (Quarterly/YTD)
  // ============================================================================
  const renderStep2 = () => {
    if (generationMode !== 'monthly') {
      return renderStep2ReportSelector();
    }

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Upload CSV Files
          </h2>
          <p className="text-gray-600 mb-6">
            Upload your sales data and optionally ad spend data
          </p>

          {/* Format Information */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Expected CSV Format</h3>
            <div className="text-sm text-blue-800 space-y-1">
              {reportType === 'cody' ? (
                <>
                  <p><strong>Cody Sales CSV:</strong> Lead source, Trade, Job Info, Contract Total, Gross Margin %, Gross Profit, ...</p>
                </>
              ) : (
                <>
                  <p><strong>Sales CSV:</strong> Date, Name, Job trade type, Volume, Lead Source, Cost, Gross Profit, GP Margin, Comments</p>
                  <p><strong>Ad Spend CSV (optional):</strong> Source name and cost columns</p>
                </>
              )}
            </div>
          </div>

          {/* Sales CSV Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sales CSV File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleSalesFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {salesFileStatus && (
              <div className={`mt-2 flex items-center text-sm ${salesFileStatus.valid ? 'text-green-600' : 'text-red-600'}`}>
                <span className="mr-2">{salesFileStatus.valid ? '✓' : '✗'}</span>
                <span>{salesFileStatus.message}</span>
              </div>
            )}
          </div>

          {/* Ad Spend CSV Upload (Standard Reports Only) */}
          {reportType === 'standard' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ad Spend CSV File <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleAdSpendFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {adSpendFileStatus && (
                <div className={`mt-2 flex items-center text-sm ${adSpendFileStatus.valid ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-2">{adSpendFileStatus.valid ? '✓' : '✗'}</span>
                  <span>{adSpendFileStatus.message}</span>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={handleBackStep}
              className="px-6 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNextStep}
              disabled={!salesFile || !salesFileStatus?.valid}
              className={`
                px-6 py-2 rounded-md font-medium transition-colors
                ${salesFile && salesFileStatus?.valid
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 2 (Quarterly/YTD): Select Saved Reports to Combine
  // ============================================================================
  const renderStep2ReportSelector = () => {
    const expectedMonths = getExpectedMonths();
    const availableMonths = new Set(availableReports.map(r => r.period.month));
    const missingMonths = expectedMonths.filter(m => !availableMonths.has(m));

    const modeLabel = generationMode === 'quarterly'
      ? `Q${selectedQuarter} ${period.year} (${expectedMonths.map(m => monthNames[m - 1]).join(', ')})`
      : `YTD January-${monthNames[period.month - 1]} ${period.year}`;

    const typeLabel = reportType === 'cody' ? 'Cody' : 'Standard';

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Select Reports to Combine
          </h2>
          <p className="text-gray-600 mb-6">
            {typeLabel} reports for {modeLabel}
          </p>

          {/* Available Reports */}
          {availableReports.length > 0 ? (
            <div className="mb-6 space-y-3">
              {expectedMonths.map(month => {
                const report = availableReports.find(r => r.period.month === month);
                return (
                  <div
                    key={month}
                    className={`p-4 rounded-lg border-2 ${
                      report
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className={`mr-3 text-lg ${report ? 'text-green-600' : 'text-gray-400'}`}>
                          {report ? '✓' : '✗'}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {monthNames[month - 1]} {period.year}
                          </div>
                          {report && (
                            <div className="text-sm text-gray-600">
                              {report.data.jobData.length} jobs · {formatCurrency(report.data.kpis.totalSales)} sales
                            </div>
                          )}
                          {!report && (
                            <div className="text-sm text-gray-400">
                              No saved report found
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mb-6 p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
              <div className="text-4xl mb-3">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Saved Reports Found
              </h3>
              <p className="text-sm text-gray-600">
                No {typeLabel.toLowerCase()} reports for {period.year} are saved yet.
                Generate monthly reports first, then combine them here.
              </p>
            </div>
          )}

          {/* Warnings */}
          {missingMonths.length > 0 && availableReports.length > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <span className="text-amber-600 mr-2 mt-0.5">⚠</span>
                <div className="text-sm text-amber-800">
                  <strong>{missingMonths.length} month{missingMonths.length > 1 ? 's' : ''} missing:</strong>{' '}
                  {missingMonths.map(m => monthNames[m - 1]).join(', ')}.
                  The combined report will only include available months.
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          {availableReports.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>Will combine {availableReports.length} report{availableReports.length > 1 ? 's' : ''}:</strong>{' '}
                {availableReports.length} of {expectedMonths.length} months available.
                Total jobs: {availableReports.reduce((sum, r) => sum + r.data.jobData.length, 0)}.
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={handleBackStep}
              className="px-6 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNextStep}
              disabled={availableReports.length === 0}
              className={`
                px-6 py-2 rounded-md font-medium transition-colors
                ${availableReports.length > 0
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Combine Reports
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Step 3: Preview & Verify
  // ============================================================================
  const renderStep3 = () => (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Preview & Verify
        </h2>
        <p className="text-gray-600 mb-6">
          Review the processed data before generating the final report
        </p>

        {/* Processing State */}
        {isProcessing && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Processing CSV files...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <span className="text-red-600 mr-2">✗</span>
              <div className="text-sm text-red-800">{error}</div>
            </div>
          </div>
        )}

        {/* Success State with Data */}
        {!isProcessing && !error && reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-600 font-medium mb-1">Jobs</div>
                <div className="text-2xl font-bold text-blue-900">
                  {reportData.jobData.length}
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="text-sm text-green-600 font-medium mb-1">Total Sales</div>
                <div className="text-2xl font-bold text-green-900">
                  {formatCurrency(reportData.kpis.totalSales)}
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="text-sm text-purple-600 font-medium mb-1">Gross Profit</div>
                <div className="text-2xl font-bold text-purple-900">
                  {formatCurrency(reportData.kpis.grossProfit)}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  {formatPercent(reportData.kpis.grossProfitPercent)}
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="text-sm text-amber-600 font-medium mb-1">Net Profit</div>
                <div className="text-2xl font-bold text-amber-900">
                  {formatCurrency(reportData.kpis.netProfit)}
                </div>
                <div className="text-xs text-amber-600 mt-1">
                  {formatPercent(reportData.kpis.netProfitPercent)}
                </div>
              </div>
            </div>

            {/* Job Data Table */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Job Data Preview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Info
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lead Source
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trade
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contract Total
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gross Profit
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Net Profit
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.jobData.slice(0, 10).map((job, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {job.jobInfo}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {job.leadSource}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {job.trade}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(job.contractTotal)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(job.grossProfit)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(job.netProfit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reportData.jobData.length > 10 && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing 10 of {reportData.jobData.length} jobs
                </p>
              )}
            </div>
          </>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={handleBackStep}
            className="px-6 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
          >
            Back
          </button>
          {!isProcessing && !error && reportData && (
            <button
              onClick={() => setStep(4)}
              className="px-6 py-2 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Generate Report
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Step 4: View Report
  // ============================================================================
  const renderStep4 = () => (
    <div className="max-w-7xl mx-auto">
      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-900">
          {getReportTitle()}
        </h2>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handlePrint}
            className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
          >
            Print
          </button>

          <button
            onClick={generatePDF}
            className="px-4 py-2 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            Export PDF
          </button>

          <button
            onClick={handleSave}
            className={`
              px-4 py-2 rounded-md font-medium transition-colors
              ${saved
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            {saved ? 'Saved ✓' : 'Save Report'}
          </button>

          <button
            onClick={handleGenerateAnother}
            className="px-4 py-2 rounded-md font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Generate Another
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef}>
        {reportData && reportType === 'standard' && (
          <MarketingReport data={reportData} />
        )}
        {reportData && reportType === 'cody' && (
          <CCMarketingReport data={reportData} />
        )}
      </div>
    </div>
  );

  // ============================================================================
  // Main Render
  // ============================================================================
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <StepIndicator />

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>
    </div>
  );
};

export default GenerateReportPage;
